import {
  Box,
  Collapse,
  Flex,
  Skeleton,
  // Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import PlanCard from '../cards/PlanCard';
import { useUserStore } from '../../store/user';
import configureAxios from '../../axiosClient';
import LeftBillingSummary from './LeftBillingSummary';
import AccountStatusTracker from './AccountStatusTracker';
import CardManager from './CardManager';
import { usePlanStore } from '../../store/plan';
import { dispatchSubscriptionToAnalytics } from '../../analytics';
import { useAuthStore } from '../../store/auth';
import UserInvitesModal from '../modals/UserInvitesModal';
import { CloseIcon } from '@chakra-ui/icons';
import { PlansType, PLANS } from '../../utils/plans';

const axiosClient = configureAxios();

type Plan = {
  planType: string;
  charges: string;
  btnText: string;
  targetPeople: string;
  planDuration: string;
};

interface AdditionalPlan {
  [key: string]: AdditionalPlanData;
}

interface AdditionalPlanData {
  planType: string;
  btnText: string;
  targetPeople: string;
}

function Plans() {
  const toast = useToast({
    position: 'top',
    variant: 'top-accent',
  });
  const {
    organisation,
    user,
    inactiveOrganisation,
    userCreateNewOrganisationView,
    setOrganisation,
    setUserCreateNewOrganisationView,
    getOrganizations,
    setInactiveOrganisation,
  } = useUserStore();
  const {
    currentPlan,
    inActiveOrgCurrentPlan,
    showPlans,
    setShowPlans,
    setCurrentPlan,
    setInActiveOrgCurrentPlan,
    setShowCancelPlans,
    setShowEnterpriseForm,
    setShowGettingStartedModal,
  } = usePlanStore();
  const { firstTime } = useAuthStore();
  const [
    billingPeriod,
    // setBillingPeriod
  ] = useState('monthly');
  const [view, setView] = useState('plans');
  const [isLoading, setIsLoading] = useState(true);
  const [submittingPlan, setSubmittingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan>({
    planType: '',
    charges: '',
    btnText: '',
    targetPeople: '',
    planDuration: '',
  });
  const [refetchPlan, setReFetchPlan] = useState<boolean>(true);
  const [userInvitesModalActive, setUserInvitesModalActive] = useState(false);
  // const [disabledPlans, setDisabledPlans] = useState<string[]>([]);

  const close = () => {
    setShowPlans(false);
    setView('plans');
    firstTime && setShowGettingStartedModal(true);
  };

  useEffect(() => {
    axiosClient
      .get('user/occupied-plans')
      .then((_) => {
        // setDisabledPlans(res?.data?.data);
      })
      .catch((err: unknown) => {
        console.log('Error -> ', err);
      });
  }, [inactiveOrganisation.organizationId, billingPeriod]);

  useEffect(() => {
    const userOrgId = inactiveOrganisation.organizationId
      ? inactiveOrganisation.organizationId
      : organisation.organizationId;

    organisation?.alias?.length > 0 &&
      axiosClient
        .get(`org/${userOrgId}/plan/read`)
        .then((res) => {
          setIsLoading(true);
          const isStatusOk = res.data.status === 'OK';
          if (res.data.status === 'LOGOUT') {
            setIsLoading(false);
            console.log('Logged Out');
          } else if (isStatusOk) {
            setIsLoading(false);
            inactiveOrganisation.organizationId
              ? setInActiveOrgCurrentPlan(res.data.data)
              : setCurrentPlan(res.data.data);
          } else {
            setIsLoading(false);
            toast({
              title: `There was an error fetching submit status plan`,
              status: 'error',
            });
          }
        })
        .catch((err: unknown) => {
          setIsLoading(false);
          console.log(err);
        });
  }, [
    organisation.organizationId,
    inactiveOrganisation.organizationId,
    refetchPlan,
  ]);

  const payForPlan = () => {
    const userOrgId = inactiveOrganisation.organizationId
      ? inactiveOrganisation.organizationId
      : organisation.organizationId;
    setSubmittingPlan(true);
    axiosClient
      .get(
        `org/${userOrgId}/plan/update/${selectedPlan?.planType}/${billingPeriod}`
      )
      .then((res) => {
        const isStatusOk = res.data.status === 'OK';
        if (isStatusOk) {
          setReFetchPlan(!refetchPlan);
          if (inactiveOrganisation.organizationId) {
            setOrganisation({
              organizationId: inactiveOrganisation.organizationId,
              alias: inactiveOrganisation.alias,
              roles: inactiveOrganisation.roles || 'member',
            });
            setInactiveOrganisation({
              organizationId: '',
              alias: '',
              roles: '',
            });
          }
          close();
          getOrganizations();
          if (
            userCreateNewOrganisationView &&
            selectedPlan.planType !== 'Pro' &&
            selectedPlan.planType !== 'Free'
          ) {
            setUserInvitesModalActive(true);
            setUserCreateNewOrganisationView(false);
          }

          if (selectedPlan.btnText === 'Start Free Trial') {
            dispatchSubscriptionToAnalytics('start_trial', {
              current_plan: selectedPlan.planType,
              previous_plan: currentPlan.name,
              subscription_price: selectedPlan.charges,
              organisation_id: organisation.organizationId,
              organisation_user_id: user.id,
              subscription_interval: billingPeriod,
            });
          } else {
            dispatchSubscriptionToAnalytics('purchase_subscription', {
              current_plan: selectedPlan.planType,
              previous_plan: currentPlan.name,
              subscription_price: selectedPlan.charges,
              organisation_id: organisation.organizationId,
              organisation_user_id: user.id,
              subscription_interval: billingPeriod,
            });
          }

          toast({
            title: `${selectedPlan?.planType} was subscribed successfully`,
            status: 'success',
          });
          setSubmittingPlan(false);
        } else {
          toast({
            title: res.data.data || `Error occurred while updating pan`,
            status: 'error',
          });
          setSubmittingPlan(false);
        }
      })
      .catch((err: any) => {
        console.log('error', err);
        toast({
          title: err.response.data.data,
          status: 'error',
        });
        setSubmittingPlan(false);
      });
  };

  const additionalPlanData: AdditionalPlan = {
    Free: {
      planType: 'Free',
      btnText: !currentPlan.name.includes('Free')
        ? 'Cancel Paid Plan'
        : 'Start For Free',
      targetPeople: 'for students & personal projects',
    },
    Paid: {
      planType: 'Paid',
      btnText: 'Contact Sales',
      targetPeople:
        'for Freelancers, Studios, or Retailers \n Get in touch to find the right strategy for your organization',
    },
  };

  return (
    <Box
      opacity={showPlans ? 1 : 0}
      visibility={showPlans ? 'visible' : 'hidden'}
      transition='all 0.5s'
      position='fixed'
      height='100vh'
      overflow='auto'
      width='100%'
      background='rgba(8, 16, 29, 0.6)'
      backdropFilter='blur(30px)'
      zIndex='100'
      padding={['40px 0px']}
      color='white'
      display='flex'
      justifyContent='center'
      alignItems='center'
    >
      <Box
        position='absolute'
        right={2}
        top={10}
        display='flex'
        justifyContent='flex-end'
        padding='0 10px'
      >
        <CloseIcon
          boxSize={5}
          cursor='pointer'
          onClick={() => {
            close();
            setUserCreateNewOrganisationView(false);
            setInactiveOrganisation({
              organizationId: '',
              alias: '',
              roles: '',
            });
          }}
        />
      </Box>

      <Box
        overflow='auto'
        display='flex'
        flexDirection='column'
        justifyContent='flex-start'
        alignItems='center'
        width='100%'
        gap={8}
      >
        <Collapse
          animateOpacity
          in={view === 'plans'}
          transition={{ enter: { delay: 0.5 } }}
        >
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='space-between'
            alignItems='center'
            gap={8}
          >
            {isLoading ? (
              <Flex justifyContent='center' gap='20px'>
                <Skeleton
                  width={['340px']}
                  startColor='#29303C'
                  endColor='transparent'
                  isLoaded={!isLoading}
                  borderRadius='30px'
                  height='600px'
                />
                <Skeleton
                  width={['475px']}
                  startColor='#29303C'
                  endColor='transparent'
                  isLoaded={!isLoading}
                  borderRadius='30px'
                  height='600px'
                />
              </Flex>
            ) : (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='flex-start'
                gap={5}
              >
                {PLANS.map((plan: PlansType, index: number) => {
                  const planName = plan.planType;
                  return (
                    <PlanCard
                      key={index}
                      current={
                        inactiveOrganisation.organizationId
                          ? planName.toLowerCase() ===
                            (inActiveOrgCurrentPlan?.name
                              ?.split('_')[0]
                              .toLowerCase() === 'premium'
                              ? 'paid'
                              : inActiveOrgCurrentPlan?.name
                                  ?.split('_')[0]
                                  .toLowerCase())
                          : planName.toLowerCase() ===
                            (currentPlan?.name?.split('_')[0].toLowerCase() ===
                            'premium'
                              ? 'paid'
                              : currentPlan?.name?.split('_')[0].toLowerCase())
                      }
                      planType={planName}
                      targetPeople={plan.targetPeople}
                      charges={plan.price}
                      btnText={plan.btnText}
                      planDuration={plan.durationText}
                      onClick={() => {
                        setSelectedPlan({
                          planType: planName,
                          charges: plan?.price?.toString() || '',
                          btnText: additionalPlanData[planName]?.btnText,
                          targetPeople:
                            additionalPlanData[planName]?.targetPeople,
                          planDuration: plan.durationText,
                        });

                        if (planName === 'Free') {
                          if (!currentPlan.name.includes('Free')) {
                            setShowCancelPlans(true);
                          } else {
                            close();
                          }
                        }
                        if (planName === 'Paid') {
                          setShowEnterpriseForm(true);
                        }
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        </Collapse>

        <Collapse
          animateOpacity
          in={view === 'payment'}
          transition={{ enter: { delay: 0.5 } }}
        >
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='flex-start'
            alignItems='center'
            gap={8}
          >
            <AccountStatusTracker setView={setView} />

            <Box
              display='flex'
              justifyContent='center'
              alignItems='flex-start'
              gap={5}
            >
              <Box
                minW={['700px']}
                padding={['20px']}
                borderRadius='16px'
                boxShadow='0px 4px 4px 0px rgba(0, 0, 0, 0.25)'
                bg='white'
              >
                {view === 'payment' && <CardManager />}
              </Box>
              <LeftBillingSummary
                selectedPlan={selectedPlan}
                submittingPlan={submittingPlan}
                billingPeriod={billingPeriod}
                btnText={
                  additionalPlanData[
                    selectedPlan?.planType as keyof typeof additionalPlanData
                  ]?.btnText
                }
                payForPlan={() => payForPlan()}
              />
            </Box>
          </Box>
        </Collapse>
      </Box>

      <UserInvitesModal
        userInvitesModalActive={userInvitesModalActive}
        setUserInvitesModalActive={setUserInvitesModalActive}
      />
    </Box>
  );
}

export default Plans;
